import axios, { AxiosError } from 'axios'
import { useNavigate } from 'react-router-dom'

import Refresh from '@/modules/auth/external/refresh'
import AuthService from '@/shared/services/auth-service'

import { Response } from '../services/api'

const axiosInstance = axios.create({
  baseURL: import.meta.env.REACT_APP_BASE_URL,
  timeout: 60000
})

axiosInstance.interceptors.request.use(
  (config) => {
    const token = AuthService.getUser()?.accessToken

    if (token && config.headers) {
      const persona = AuthService.getPersona()
      config.headers['Authorization'] = 'Bearer ' + token
      config.headers['X-CompanyID'] = persona?.companyId
      config.headers['X-PersonaID'] = persona?.personaId
      config.headers['X-PersonaType'] = persona?.personaType
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (response) => {
    return response
  },
  async (err) => {
    const originalConfig = err.config

    originalConfig._retryCount = 0

    if (
      (err.response.status === 403 || err.response.status === 401) &&
      originalConfig._retryCount < 2 &&
      originalConfig.url !== `/v1/token/refresh/`
    ) {
      originalConfig._retryCount++

      if (
        window.location.pathname === '/login' ||
        window.location.pathname === '/' ||
        window.location.pathname === 'index.html'
      ) {
        AuthService.logout()
        const navigate = useNavigate()
        navigate('/')
      }

      try {
        const refreshToken = AuthService.getUser()?.refreshToken
        const response = await Refresh.refresh(refreshToken!)

        const { accessToken } = response

        AuthService.setUser({ accessToken })

        return axiosInstance(originalConfig)
      } catch (_error) {
        return Promise.reject(_error)
      }
    }

    return Promise.reject(err)
  }
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const customAxios: any = Object.assign({}, axiosInstance)
const methods = ['get', 'post', 'put', 'patch', 'delete']
methods.forEach((method) => {
  customAxios[method] = async <T>(...args: unknown[]): Response<T> => {
    try {
      const response = await axiosInstance[method](...args)
      return [response, null]
    } catch (error) {
      return [null, error as AxiosError<unknown>]
    }
  }
})

export default customAxios
